import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ToggleLeft, Grid3x3, Shield, Award, Clock, Wrench, CheckCircle, Star } from "lucide-react";

export default function ElectricalAccessories() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const features = [
    { icon: ToggleLeft, key: "switchesSockets", color: "#ffde21" },
    { icon: Grid3x3, key: "electricalPanels", color: "#ffde21" },
    { icon: Shield, key: "electricalBreakers", color: "#ffde21" }
  ];

  const brands = [
    { name: dir === "rtl" ? "علامات عالمية" : "Global Brands", icon: Star },
    { name: dir === "rtl" ? "جودة مضمونة" : "Guaranteed Quality", icon: Award },
    { name: dir === "rtl" ? "ضمان طويل" : "Long Warranty", icon: CheckCircle }
  ];

  const benefits = [
    {
      icon: Shield,
      title: dir === "rtl" ? "أمان معتمد" : "Certified Safety",
      desc: dir === "rtl" ? "منتجات معتمدة ومطابقة لأعلى معايير السلامة" : "Certified products meeting highest safety standards"
    },
    {
      icon: Award,
      title: dir === "rtl" ? "جودة فاخرة" : "Premium Quality",
      desc: dir === "rtl" ? "تصاميم حديثة تجمع بين الفخامة والمتانة" : "Modern designs combining luxury and durability"
    },
    {
      icon: Wrench,
      title: dir === "rtl" ? "سهولة التركيب" : "Easy Installation",
      desc: dir === "rtl" ? "تركيب سهل وسريع من قبل فريقنا المتخصص" : "Quick and easy installation by our specialized team"
    },
    {
      icon: Clock,
      title: dir === "rtl" ? "عمر افتراضي طويل" : "Long Lifespan",
      desc: dir === "rtl" ? "منتجات متينة تدوم لسنوات طويلة" : "Durable products lasting for many years"
    }
  ];

  return (
    <div dir={dir} className="min-h-screen" style={{ background: '#fafcf0' }}>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #2d2d2b 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
            {/* Image Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                  style={{ background: '#ffde21' }}
                ></div>
                <img
                  src="/images/accessoriesimg.webp"
                  alt={t("electricalAccessories")}
                  className="relative rounded-3xl shadow-2xl w-full object-cover"
                  style={{ border: '3px solid #ffde21' }}
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
            </motion.div>

            {/* Text Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="inline-block mb-4">
                <span
                  className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                  style={{
                    background: 'rgba(255, 222, 33, 0.2)',
                    color: '#2d2d2b'
                  }}
                >
                  <Zap size={16} />
                  {dir === "rtl" ? "جودة فائقة" : "Superior Quality"}
                </span>
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight"
                style={{ color: '#2d2d2b' }}
              >
                {t("electricalAccessories")}
              </h1>

              <p
                className="text-lg md:text-xl leading-relaxed mb-8"
                style={{ color: '#2d2d2b', opacity: 0.8 }}
              >
                {dir === "rtl"
                  ? "مفاتيح وأباريز ولوحات كهربائية حديثة تجمع بين التصميم الراقي والأداء العالي والمتانة الفائقة"
                  : "Modern switches, sockets, and electrical panels combining elegant design with high performance and superior durability"}
              </p>

              <button
                className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#ffde21',
                  color: '#2d2d2b'
                }}
              >
                {dir === "rtl" ? "تصفح المنتجات" : "Browse Products"}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "منتجاتنا" : "Our Products"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "تشكيلة واسعة من الإكسسوارات الكهربائية عالية الجودة"
                : "Wide range of high-quality electrical accessories"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group cursor-pointer"
                  style={{
                    background: 'white',
                    border: '2px solid rgba(255, 222, 33, 0.2)'
                  }}
                >
                  {/* Icon Circle */}
                  <div
                    className="w-20 h-20 rounded-xl flex items-center justify-center mb-6 mx-auto transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={40} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>

                  {/* Feature Description */}
                  <p
                    className="text-base leading-relaxed text-center"
                    style={{ color: '#2d2d2b' }}
                  >
                    {t(`electricalAccessories_features.${feature.key}`)}
                  </p>

                  {/* Decorative Element */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-all duration-300 group-hover:h-2"
                    style={{ background: '#ffde21' }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section
        className="py-16 md:py-24 relative overflow-hidden"
        style={{ background: '#2d2d2b' }}
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21),
                             linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21)`,
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#ffde21' }}
            >
              {dir === "rtl" ? "لماذا منتجاتنا؟" : "Why Our Products?"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'white', opacity: 0.9 }}
            >
              {dir === "rtl"
                ? "نوفر أفضل المنتجات من علامات تجارية عالمية موثوقة"
                : "We provide the best products from trusted global brands"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {brands.map((brand, index) => {
              const Icon = brand.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-8 rounded-2xl text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group"
                  style={{
                    background: 'rgba(250, 252, 240, 0.05)',
                    border: '2px solid rgba(255, 222, 33, 0.3)'
                  }}
                >
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={36} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>
                  <h3
                    className="text-xl font-bold"
                    style={{ color: 'white' }}
                  >
                    {brand.name}
                  </h3>

                  {/* Corner Accent */}
                  <div
                    className="absolute top-4 right-4 w-2 h-2 rounded-full"
                    style={{ background: '#ffde21', opacity: 0.5 }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 md:py-24" style={{ background: 'rgba(255, 222, 33, 0.05)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "المزايا الرئيسية" : "Key Benefits"}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="p-8 rounded-2xl transition-all duration-300 hover:shadow-xl"
                  style={{
                    background: 'white',
                    border: '2px solid rgba(255, 222, 33, 0.3)'
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'rgba(255, 222, 33, 0.2)',
                        border: '2px solid #ffde21'
                      }}
                    >
                      <Icon size={28} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                    </div>
                    <div>
                      <h3
                        className="text-xl font-bold mb-2"
                        style={{ color: '#2d2d2b' }}
                      >
                        {benefit.title}
                      </h3>
                      <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                        {benefit.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Showcase Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "مجموعة متنوعة من التصاميم" : "Diverse Design Collection"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "اختر من بين مجموعة واسعة من الأنماط والألوان"
                : "Choose from a wide range of styles and colors"}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { name: dir === "rtl" ? "كلاسيكي" : "Classic", color: "#e8e8e8" },
              { name: dir === "rtl" ? "عصري" : "Modern", color: "#2d2d2b" },
              { name: dir === "rtl" ? "فاخر" : "Luxury", color: "#d4af37" },
              { name: dir === "rtl" ? "بسيط" : "Minimal", color: "#ffffff" }
            ].map((style, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl text-center transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{
                  background: 'white',
                  border: '3px solid rgba(255, 222, 33, 0.3)'
                }}
              >
                <div
                  className="w-20 h-20 rounded-xl mx-auto mb-4 transition-transform duration-300 hover:rotate-12"
                  style={{
                    background: style.color,
                    border: style.color === "#ffffff" ? '2px solid #e8e8e8' : 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                ></div>
                <p
                  className="font-bold"
                  style={{ color: '#2d2d2b' }}
                >
                  {style.name}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Zap size={60} style={{ color: '#ffde21', margin: '0 auto', marginBottom: '1.5rem' }} />
            <h2
              className="text-3xl md:text-4xl font-black mb-6"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "جاهز للترقية؟" : "Ready to Upgrade?"}
            </h2>
            <p
              className="text-lg md:text-xl mb-8"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "احصل على مفاتيح وأباريز عصرية تضيف لمسة فاخرة لمنزلك أو مكتبك"
                : "Get modern switches and sockets that add a luxurious touch to your home or office"}
            </p>
            <Link to="/contact">
              <button
                className="px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#ffde21',
                  color: '#2d2d2b'
                }}
              >
                {t("homepage.buttons.contactUs")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}